import { useCallback, useEffect, useState } from 'react'
import type { List } from 'app/generated/graphql'
import { SYSTEM_LIST_QUERY } from 'app/graphql/lists'
import { gqlQuery } from 'app/lib/graphqlClient'
import type { SystemListDef } from 'app/types/navigation'
import { useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { systemListId } from 'lib/systemLists'

interface Read {
  id: string
  list: List | undefined
  failed: boolean
}

// The board knows its exact id, so it reads the list by id and never depends on the `lists` query,
// which can leave system lists out (visibility) and is not the source of the stages the board edits.
export function useSystemList(
  def: SystemListDef,
  { enabled = true }: { enabled?: boolean } = {},
): { list: List | undefined; failed: boolean; reload: () => void } {
  const workspace = useWorkspace()
  const id = enabled && workspace?.id ? systemListId(def, workspace.id) : undefined
  const [read, setRead] = useState<Read>()
  const [reloads, setReloads] = useState(0)

  useEffect(() => {
    if (!id) return
    // A read for a workspace the user has since left, or one a reload replaced, must not overwrite the newer one.
    let stale = false
    bg(async () => {
      const { data, error } = await gqlQuery(
        SYSTEM_LIST_QUERY,
        { id },
        { requestPolicy: `network-only`, suppressErrorToast: true },
      )
      if (stale) return
      // The board's consumers take the model's `List`, whose optional fields are undefined where this read has null.
      const list = error || !data?.list ? undefined : (data.list as List)
      // A reload that fails after a good read keeps the list the board is showing instead of unmounting it.
      setRead((previous) => {
        const kept = list ?? (previous?.id === id ? previous.list : undefined)
        return { id, list: kept, failed: !kept }
      })
    })
    return () => {
      stale = true
    }
  }, [id, reloads])

  // Stable, so a caller that memoizes on it (useTreatmentStages) does not rebuild every render.
  const reload = useCallback(() => setReloads((count) => count + 1), [])
  const current = read?.id === id ? read : undefined
  return { list: current?.list, failed: current?.failed ?? false, reload }
}
